import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    it: {
      title: 'Privacy Policy',
      lastUpdate: 'Ultimo aggiornamento: Febbraio 2026',
      sections: [
        {
          title: 'Introduzione',
          content: 'Huacaya ("noi", "nostro") si impegna a proteggere la privacy dei visitatori del sito www.huacaya.io. Questa policy spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali.'
        },
        {
          title: 'Dati che raccogliamo',
          content: 'Raccogliamo i seguenti tipi di dati:',
          list: [
            'Email: quando ti iscrivi alla newsletter',
            'Dati di navigazione: pagine visitate, tempo di permanenza (tramite Google Analytics)',
            'Dati di acquisto: gestiti interamente da Gumroad, nostro processore di pagamento'
          ]
        },
        {
          title: 'Cookie e Tecnologie di Tracciamento',
          content: 'Utilizziamo:',
          list: [
            'Google Analytics: per analizzare il traffico del sito',
            'Meta Pixel: per misurare l\'efficacia delle campagne pubblicitarie',
            'Cookie tecnici: per il funzionamento del sito'
          ]
        },
        {
          title: 'Come utilizziamo i tuoi dati',
          content: 'I tuoi dati vengono utilizzati per:',
          list: [
            'Inviarti la newsletter (se iscritto)',
            'Migliorare i nostri servizi',
            'Analizzare le performance del sito',
            'Fornirti accesso alle guide acquistate'
          ]
        },
        {
          title: 'I tuoi diritti',
          content: 'In conformità al GDPR, hai il diritto di:',
          list: [
            'Accedere ai tuoi dati personali',
            'Rettificare dati inesatti',
            'Richiedere la cancellazione dei tuoi dati',
            'Revocare il consenso in qualsiasi momento',
            'Opporti al trattamento dei dati'
          ]
        },
        {
          title: 'Contatti',
          content: 'Per qualsiasi domanda sulla privacy, contattaci a: info@huacaya.io'
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: February 2026',
      sections: [
        {
          title: 'Introduction',
          content: 'Huacaya ("we", "our") is committed to protecting the privacy of visitors to www.huacaya.io. This policy explains how we collect, use and protect your personal data.'
        },
        {
          title: 'Data we collect',
          content: 'We collect the following types of data:',
          list: [
            'Email: when you subscribe to our newsletter',
            'Browsing data: pages visited, time spent (via Google Analytics)',
            'Purchase data: handled entirely by Gumroad, our payment processor'
          ]
        },
        {
          title: 'Cookies and Tracking Technologies',
          content: 'We use:',
          list: [
            'Google Analytics: to analyze site traffic',
            'Meta Pixel: to measure advertising campaign effectiveness',
            'Technical cookies: for site functionality'
          ]
        },
        {
          title: 'How we use your data',
          content: 'Your data is used to:',
          list: [
            'Send you newsletters (if subscribed)',
            'Improve our services',
            'Analyze site performance',
            'Provide access to purchased guides'
          ]
        },
        {
          title: 'Your rights',
          content: 'Under GDPR, you have the right to:',
          list: [
            'Access your personal data',
            'Rectify inaccurate data',
            'Request deletion of your data',
            'Withdraw consent at any time',
            'Object to data processing'
          ]
        },
        {
          title: 'Contact',
          content: 'For any privacy questions, contact us at: info@huacaya.io'
        }
      ]
    }
  };

  const t = content[language] || content.it;

  return (
    <main className="pt-20 min-h-screen bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#C9B896]" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {t.title}
            </h1>
          </div>
          <p className="text-[#86868B] mb-12">{t.lastUpdate}</p>

          {/* Sections */}
          <div className="space-y-10">
            {t.sections.map((section, index) => (
              <motion.section
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h2 className="text-xl font-semibold text-white mb-3">
                  {section.title}
                </h2>
                <p className="text-[#A1A1A6] leading-relaxed mb-3">
                  {section.content}
                </p>
                {section.list && (
                  <ul className="list-disc list-inside space-y-2 text-[#A1A1A6]">
                    {section.list.map((item, i) => (
                      <li key={i} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                )}
              </motion.section>
            ))}
          </div>

          {/* Contact Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16 p-6 bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C]"
          >
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#C9B896]" />
              <span className="text-white font-medium">info@huacaya.io</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
